/**
 * What a guarantor phone control holds depends on how it got there: an ngx-intl-tel-input object when
 * typed (e164Number empty while the number is unparseable), a plain string once the remove-leading-zero
 * directive or an identity lookup has rewritten it, or null/'' when blank. Reduce all of them to digits.
 *
 * For an object only e164Number and number count. internationalNumber is deliberately ignored: picking a
 * country in ngx-intl-tel-input fills it with just the dial code ("+234"), even when nothing has been typed.
 */
export function phoneDigits(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    return String(value.e164Number || value.number || '').replace(/\D/g, '');
  }
  return String(value).replace(/\D/g, '');
}

export type GuarantorPhoneProblem = 'incomplete' | 'required' | null;

/**
 * Whether the guarantor's phone details can be submitted. The backend takes the alternate number as the
 * mobile number when the mobile is blank, so an alternate number alone is fine, and then nothing the
 * mobile field holds (stray space, half-typed number) may block it.
 *
 * @param typedMobile      the raw mobile control value
 * @param payloadMobile    the mobile number as it will be sent
 * @param payloadAlternate the alternate number as it will be sent
 */
export function guarantorPhoneProblem(
  typedMobile: any,
  payloadMobile: any,
  payloadAlternate: any,
): GuarantorPhoneProblem {
  if (phoneDigits(payloadMobile)) return null;
  if (phoneDigits(payloadAlternate)) return null;
  return phoneDigits(typedMobile) ? 'incomplete' : 'required';
}

/**
 * The guarantor has no field that says which number is the default, so the default is expressed by order: the
 * number marked as default goes out as mobileNumber (what every screen, search and message uses) and the other
 * one as alternatePhoneNumber. Choosing the alternate as default therefore swaps the two. Nothing changes when the
 * mobile is the default, when there is no usable alternate to promote, or when the alternate is the same number.
 */
export function applyDefaultPhone(
  mobileNumber: any,
  alternatePhoneNumber: any,
  defaultSlot: string | null | undefined,
): { mobileNumber: any; alternatePhoneNumber: any } {
  const unchanged = { mobileNumber, alternatePhoneNumber };
  if (defaultSlot !== 'alternatePhoneNumber') return unchanged;
  if (!phoneDigits(alternatePhoneNumber)) return unchanged;
  if (phoneDigits(alternatePhoneNumber) === phoneDigits(mobileNumber)) return unchanged;
  return {
    mobileNumber: alternatePhoneNumber,
    alternatePhoneNumber: phoneDigits(mobileNumber) ? mobileNumber : null,
  };
}
